import axios from "axios";
import { Fragment, useEffect, useState } from "react";
import MapView from "../MapView";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-hot-toast";
import { FaChevronDown, FaPowerOff } from "react-icons/fa6";
import { useUserContext } from "../../context/UserContext";
import DeviceIdInput from "../DeviceIdInput";

const locations = ["development", "production"];

export default function DeviceConfig() {
  // ...existing state
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const { token, deviceId: device_id } = useUserContext();
  const [deviceId, setDeviceId] = useState(device_id);
  const [datasetLocation, setDatasetLocation] = useState(locations[0]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [numberAttached, setNumberAttached] = useState(availableNumbers[0]);
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const [isNumberDropdownOpen, setIsNumberDropdownOpen] = useState(false);
  const [minMaxValues, setMinMaxValues] = useState({});
  const [sensordata, setSensordata] = useState({});
  const [instanceId, setInstanceId] = useState("");
  const [scenarioCode, setScenarioCode] = useState("");
  const [recheckValue, setRecheckValue] = useState("");

  useEffect(() => {
    if (!token) return;
    if (!deviceId || deviceId.length < 12) return;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/deviceconfig/attached-number/${deviceId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        setAvailableNumbers(res.data.data);
        setNumberAttached(res.data.data[0]);
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.message ?? err.message);
      }
    })();

    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/deviceconfig/get-specific-values/${deviceId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setMinMaxValues(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setMinMaxValues(err.response.data.defaults);
          return;
        }
        console.error(err);
        toast.error(err.response?.data?.message ?? err.message);
      }
    })();
  }, [deviceId, token]);

  useEffect(() => {
    if (!token) return;
    if (!deviceId || deviceId.length < 12) return;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/deviceconfig/get-last-sensor-data/${deviceId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        delete res.data.createdAt;
        delete res.data.updatedAt;
        res.data.timestamp = new Date(res.data.timestamp).toLocaleString(
          "en-GB",
        );
        setSensordata(res.data);
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.message ?? err.message);
      }
    })();
  }, [deviceId, token]);

  function changeDatasetLocation(datasetLocation) {
    setDatasetLocation(datasetLocation);
    setIsDatasetDropdownOpen(false);
  }
  function attachNumber(num) {
    setNumberAttached(num);
    setIsNumberDropdownOpen(false);
  }
  function attachMobileNumber() {
    if (!token) return;
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      alert("Enter Valid Device ID");
      return;
    }
    if (!numberAttached) {
      alert("Select a number");
      return;
    }
    (async () => {
      try {
        let res = await axios.post(
          `${process.env.REACT_APP_API_URL}/deviceconfig/transfer-device`,
          {
            device_id: deviceId,
            new_customer_mobile_number: numberAttached,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        toast.success("Number attached successfully");
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message ?? e.message);
      }
    })();
  }
  async function saveMinMaxValues(e) {
    e.preventDefault();
    if (!token) return;
    const formElements = e.target.elements;
    let emergency = {};
    let master = {};
    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i];
      if (element.name === "" || element.value.trim() === "") continue;
      if (element.getAttribute("data-type") === "master")
        master[element.name] = Number(element.value);
      else if (element.getAttribute("data-type") === "emergency")
        emergency[element.name] = Number(element.value);
    }
    if (Object.keys(emergency).length === 0 && Object.keys(master).length === 0)
      return toast.error("No updated value found");

    emergency = { ...minMaxValues.emergency, ...emergency };
    master = { ...minMaxValues.master, ...master };
    try {
      let res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/deviceconfig/update-specific-values`,
        {
          emergency,
          master,
          device_id: deviceId,
          deviceOnly: false,
          changeInBackup: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status !== 200) throw new Error(res.data.message);
      toast.success("Min Max Values Saved successfully");
      setMinMaxValues({ emergency, master });
      e.target.reset();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message);
    }
  }

  async function sendRecheckRequest(){
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      toast.error("Enter Valid Device ID");
      return;
    }
    try{
      let res = await axios.post(
        `${process.env.REACT_APP_API_URL}/deviceconfig/recheck-device`,
        {
          device_id: deviceId,
          instance_id: instanceId,
          scenario_code: scenarioCode,
          recheck_value: recheckValue,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status !== 200) throw new Error(res.data.message);
      toast.success("Recheck Request Sent successfully");
    }catch(e){
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message);
    }
  }

  async function setDeviceDefaultOn(){
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      toast.error("Enter Valid Device ID");
      return;
    }
    try{
      let res = await axios.post(
        `${process.env.REACT_APP_API_URL}/deviceconfig/default-on`,
        {
          device_id: deviceId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status !== 200) throw new Error(res.data.message);
      toast.success("Device Default On Set successfully");
    }catch(e){
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message);
    }
  }

  async function factoryReset() {
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 12) {
      toast.error("Enter Valid Device ID");
      return;
    }
    confirmAlert({
      title: "Confirm",
      message: "Are you sure you want to reset this device?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            factoryResetDevice();
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
    async function factoryResetDevice() {
      try {
        let res = await axios.post(
          `${process.env.REACT_APP_API_URL}/deviceconfig/factory-reset`,
          {
            device_id: deviceId,
            factory_reset: 0,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        toast.success("Device factory reset successfully");
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message ?? e.message);
      }
    }
  }

  return (
    <div className="relative mt-20 w-full">
      <DeviceIdInput deviceId={deviceId} setDeviceId={setDeviceId} />
      {deviceId && deviceId.length >= 12 && (
        <div className="px-5 w-full">
          <h2 className="mt-10 text-2xl">
            Change Dataset Location and MQTT URL
          </h2>
          <div className="flex relative gap-10 items-end p-5 rounded-lg shadow-lg">
            <div id="datasetDropdown" className="relative w-max border-2">
              <button
                onClick={() => setIsDatasetDropdownOpen(!isDatasetDropdownOpen)}
                className="flex gap-2 justify-between items-center py-2 px-4 w-full font-bold rounded"
              >
                <span className="capitalize">{datasetLocation}</span>
                <FaChevronDown />
              </button>
              <ul
                className="absolute p-2 w-full bg-gray-100 rounded divide-y divide-black cursor-pointer"
                style={{ display: isDatasetDropdownOpen ? "block" : "none" }}
              >
                {locations.map((loc, i) => (
                  <li
                    key={i}
                    className="py-1 hover:bg-gray-200"
                    onClick={() => changeDatasetLocation(loc)}
                  >
                    {loc}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-lg">Full MQTT URL</p>
              <input
                type="url"
                placeholder="mqtts://mqtt-dev.komsup.com:8883/"
                className="py-1.5 px-2 w-96 text-lg border border-black"
              />
            </div>
            <button className="py-2 px-5 text-lg font-bold text-white bg-indigo-500 rounded hover:bg-indigo-700 active:bg-indigo-600">
              SEND
            </button>
          </div>
          <h2 className="mt-10 text-2xl">Set Device Default On</h2>
          <div className="p-5 rounded-lg shadow-lg">
            <button
              onClick={setDeviceDefaultOn}
              className="py-2 px-5 text-lg font-bold text-white bg-indigo-500 rounded hover:bg-indigo-700 active:bg-indigo-600"
            >
              Set Default On
            </button>
          </div>
          <h2 className="mt-10 text-2xl">Reboot Device</h2>
          <div className="p-5 rounded-lg shadow-lg">
            <button
              onClick={async () => {
                if (!deviceId) {
                  toast.error('Please enter a device ID');
                  return;
                }
                confirmAlert({
                  title: 'Confirm Reboot',
                  message: 'Are you sure you want to reboot this device?',
                  buttons: [
                    {
                      label: 'Yes',
                      onClick: async () => {
                        try {
                          const response = await axios.post(
                            `${process.env.REACT_APP_API_URL}/deviceconfig/reboot`,
                            { device_id: deviceId, reboot: 1 },
                            {
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                            }
                          );
                          if (response.status === 200) {
                            toast.success('Device reboot initiated successfully');
                          } else {
                            throw new Error(response.data?.message || 'Failed to initiate reboot');
                          }
                        } catch (error) {
                          console.error('Reboot error:', error);
                          toast.error(error.response?.data?.message || 'Failed to reboot device');
                        }
                      },
                    },
                    {
                      label: 'No',
                      onClick: () => {},
                    },
                  ],
                });
              }}
              className="py-2 px-5 text-lg font-bold text-white bg-red-600 rounded hover:bg-red-700 active:bg-red-600"
            >
              Reboot Device
            </button>
          </div>
          <h2 className="mt-10 text-2xl">Send Recheck Request</h2>
          <div className="p-5 rounded-lg shadow-lg">
            <div className="flex gap-5">
              <input type="text" value={instanceId} onChange={(e) => setInstanceId(e.target.value)} placeholder="Instance ID" className="py-1.5 px-2 w-45 text-lg border border-black" />
              <input type="text" value={scenarioCode} onChange={(e) => setScenarioCode(e.target.value)} placeholder="Scenario Code" className="py-1.5 px-2 w-45 text-lg border border-black" />
              <input type="text" value={recheckValue} onChange={(e) => setRecheckValue(e.target.value)} placeholder="Recheck Value" className="py-1.5 px-2 w-45 text-lg border border-black" />
            </div>
            <button
              onClick={sendRecheckRequest}
              className="py-2 px-5 mt-5 text-lg font-bold text-white bg-indigo-500 rounded hover:bg-indigo-700 active:bg-indigo-600"
            >
              Send Recheck Request
            </button>
          </div>
          <h2 className="mt-10 text-2xl">Reset device to factory settings</h2>
          <div className="p-5 rounded-lg shadow-lg">
            <button
              onClick={factoryReset}
              className="py-2 px-5 text-lg font-bold text-white bg-red-500 rounded hover:bg-red-700 active:bg-red-600"
            >
              Factory Reset
            </button>
          </div>
          <h2 className="mt-10 text-2xl">Attach mobile number to device</h2>
          <div className="p-5 rounded-lg shadow-lg">
            <div className="flex relative gap-3 items-center">
              <span>Mobile No: </span>
              <div className="relative">
                <input
                  type="tel"
                  className="p-2 px-4 border-2 shrink-0 grow-0 basis-44"
                  value={numberAttached}
                  onFocus={() => setIsNumberDropdownOpen(true)}
                  onChange={(e) => setNumberAttached(e.target.value)}
                />
                {isNumberDropdownOpen && (
                  <ul className="absolute p-2 w-full bg-gray-100 rounded divide-y divide-black shadow-lg cursor-pointer">
                    {availableNumbers
                      .filter((num) => num.includes(numberAttached))
                      .map((num, i) => (
                        <li
                          key={i}
                          className="py-1 hover:bg-gray-200"
                          onClick={() => attachNumber(num)}
                        >
                          {num}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              <button
                className="py-2 px-5 ml-12 text-lg font-bold text-white bg-indigo-500 rounded hover:bg-indigo-700 active:bg-indigo-600"
                onClick={attachMobileNumber}
              >
                Attach
              </button>
            </div>
          </div>
          <h2 className="mt-10 text-2xl">Change location of device</h2>
          <div className="p-5 rounded-lg shadow-lg">
            <MapView
              latitude={latitude}
              longitude={longitude}
              onMapClick={({ lat, lng }) => {
                setLatitude(lat);
                setLongitude(lng);
              }}
            />
            <div className="mt-4 flex gap-6 items-center">
              <span>Latitude: {latitude ?? '--'}</span>
              <span>Longitude: {longitude ?? '--'}</span>
              <button
                className="py-2 px-5 ml-12 text-lg font-bold text-white bg-indigo-500 rounded hover:bg-indigo-700 active:bg-indigo-600"
                onClick={async () => {
                  if (!latitude || !longitude) {
                    alert('Please select a location on the map.');
                    return;
                  }
                  try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/deviceconfig/location-change`, {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        device_id: deviceId,
                        location: { latitude, longitude }
                      })
                    });
                    console.log(response);
                    if (response.ok) {
                      toast.success('Device location changed successfully!');
                    } else {
                      const data = await response.json();
                      toast.error(data.message || 'Failed to change device location.');
                    }
                  } catch (err) {
                    toast.error('Network error. Please try again.');
                  }
                }}
              >
                Change
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <form onSubmit={saveMinMaxValues}>
              <h2 className="mt-10 text-2xl">Set Min Max Values</h2>
              <div className="p-5 rounded-lg shadow-lg">
                <div className="space-y-1 w-min">
                  <div className="flex gap-3 justify-evenly items-center">
                    <h2 className="text-xl font-semibold">Emergency</h2>
                    <h2>Current Value</h2>
                    <h2>Set Value</h2>
                  </div>
                  {Object.keys(minMaxValues.emergency ?? Object()).map(
                    (key, i) => (
                      <div key={i} className="flex gap-3 items-center">
                        <span className="w-40">{key}</span>
                        <input
                          readOnly
                          value={minMaxValues.emergency[key]}
                          className="p-2 px-4 w-36 border-2 disabled:bg-gray-200 read-only:bg-gray-200"
                        />
                        <input
                          className="p-2 px-4 w-36 border-2"
                          data-type="emergency"
                          name={key}
                        />
                      </div>
                    ),
                  )}
                </div>
                <div className="mt-5 space-y-1 w-min">
                  <div className="flex gap-3 justify-evenly items-center">
                    <h2 className="text-xl font-semibold">Master</h2>
                    <h2>Current Value</h2>
                    <h2>Set Value</h2>
                  </div>
                  {Object.keys(minMaxValues.master ?? Object()).map(
                    (key, i) => (
                      <div key={i} className="flex gap-3 items-center">
                        <span className="w-48">{key}</span>
                        <input
                          value={minMaxValues.master[key]}
                          readOnly
                          className="p-2 px-4 w-28 border-2 read-only:bg-gray-200"
                        />
                        <input
                          className="p-2 px-4 w-36 border-2"
                          data-type="master"
                          name={key}
                        />
                      </div>
                    ),
                  )}
                </div>
                <div className="flex gap-4 mt-5">
                  <button
                    type="submit"
                    className="py-2 px-5 text-lg font-bold text-white bg-indigo-500 rounded hover:bg-indigo-700 active:bg-indigo-600"
                  >
                    Save Min-Max Values
                  </button>
                </div>
              </div>
            </form>
            <div>
              <h1 className="mt-10 text-2xl">Latest Sensor Data</h1>
              <div className="grid grid-cols-2 p-5 divide-y w-fit">
                {Object.entries(sensordata).map(([key, value], i) => (
                  <Fragment key={i}>
                    <span className="py-2">{key}</span>
                    <span className="py-2">{value ? value.toString() : ''}</span>
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
